import {useCallback, useMemo} from 'react';
import {QueryStatus} from '@reduxjs/toolkit/query/react';
import assert from 'assert-ts';
import {HttpError} from 'api/http/types';
import type {Agent, SaveAgentOptions} from '../types';
import {
  useDeleteAgentMutation,
  useGetAgentQuery,
  usePostAgentMutation,
  usePutAgentMutation,
} from '../agentApi';
import {removeMentionedIn} from '../functions';
import type {AgentState} from '../reducer/agentReducer';

export type SaveAgentFunc = (
  agent: Agent,
  options: SaveAgentOptions,
) => Promise<Agent>;

export type DeleteAgentFunc = (agent: Agent) => Promise<void>;

export const useAgent = (
  agentId?: string | null,
  /**
   * Return intermediate result as loaded state, i.e. agent without
   * work summary data loaded (just title and roles)
   */
  returnPartial: 'partial' | 'full' = 'full',
  filterLinks: 'mentionedIn' | undefined = undefined,
): {
  /** Agent enhanced with work summaries as they are loaded */
  agent: AgentState;
  /** Loaded agent, with "empty" works */
  agentEmptyWorks: AgentState;
  saveAgent: SaveAgentFunc;
  deleteAgent: DeleteAgentFunc;
} => {
  const {data: agentData, status: agentStatus} = useGetAgentQuery(
    {id: agentId!},
    {skip: !agentId},
  );
  const [putAgent] = usePutAgentMutation();
  const [postAgent] = usePostAgentMutation();
  const [deleteAgentMutation] = useDeleteAgentMutation();

  /** Save agent to backend and update reducer state if successful */
  const handleSaveAgent = useCallback<SaveAgentFunc>(
    (agent, option) => {
      const apiCall: Promise<Agent> = agentId
        ? putAgent({agent, options: option}).unwrap()
        : postAgent({agent, options: option}).unwrap();

      return apiCall.then(updatedAgent => {
        return returnPartial === 'full'
          ? removeMentionedIn(updatedAgent)
          : updatedAgent;
      });
    },
    [agentId, putAgent, postAgent, returnPartial],
  );

  /** Delete agent from backend and update reducer state if successful */
  const handleDeleteAgent = useCallback<DeleteAgentFunc>(
    agent => {
      return deleteAgentMutation(
        assert(agent.id, 'Expected agent to have an id', {agent}),
      ).then(() => {
        return;
      });
    },
    [deleteAgentMutation],
  );

  const agentDataLoad: AgentState = useMemo(() => {
    switch (agentStatus) {
      case QueryStatus.uninitialized:
        return {status: 'NotLoaded'};
      case QueryStatus.pending:
        return {status: 'Loading'};
      case QueryStatus.rejected:
        return {
          status: 'Failed',
          error: new HttpError(503, '', 'Failed to load agent'),
        };
      case QueryStatus.fulfilled:
        return agentData
          ? filterLinks === 'mentionedIn'
            ? {
                status: 'Loaded',
                data: removeMentionedIn(agentData),
              }
            : {
                status: 'Loaded',
                data: agentData,
              }
          : {
              status: 'Failed',
              error: new HttpError(503, '', 'Loading agent failed'),
            };
    }
  }, [agentData, agentStatus, filterLinks]);

  return useMemo(() => {
    return {
      agent: agentDataLoad,
      agentEmptyWorks: agentDataLoad,
      saveAgent: handleSaveAgent,
      deleteAgent: handleDeleteAgent,
    };
  }, [agentDataLoad, handleSaveAgent, handleDeleteAgent]);
};
